import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  FolderOpen,
  Plus,
  Package,
  Trash2,
  MoreVertical,
  Copy,
  ExternalLink,
  Globe,
  Lock,
  Users,
  User,
} from 'lucide-react'
import { api, Collection } from '../utils/api'

type TabType = 'public' | 'my'

export default function CollectionsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('public')
  const [publicCollections, setPublicCollections] = useState<Collection[]>([])
  const [myCollections, setMyCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNewModal, setShowNewModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newIsPublic, setNewIsPublic] = useState(false)
  const [creating, setCreating] = useState(false)
  const [menuOpen, setMenuOpen] = useState<string | null>(null)

  const token = localStorage.getItem('auth_token') || 'demo-token'
  const isAuthenticated = !!localStorage.getItem('auth_token')

  useEffect(() => {
    loadCollections()
  }, [activeTab])

  const loadCollections = async () => {
    setLoading(true)
    setError(null)
    try {
      if (activeTab === 'public') {
        const response = await api.getPublicCollections()
        setPublicCollections(response.items || [])
      } else {
        const response = await api.getCollections(token)
        setMyCollections(response.items || [])
      }
    } catch (err) {
      console.error('Failed to load collections:', err)
      setError('Failed to load collections')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) return

    setCreating(true)
    try {
      const newCollection = await api.createCollection(token, {
        org_id: 'default',
        name: newName,
        description: newDescription || undefined,
        is_public: newIsPublic,
      })
      setMyCollections([...myCollections, newCollection])
      if (newIsPublic) {
        setPublicCollections([...publicCollections, newCollection])
      }
      setShowNewModal(false)
      setNewName('')
      setNewDescription('')
      setNewIsPublic(false)
      setActiveTab('my')
    } catch (err) {
      console.error('Failed to create collection:', err)
      setError('Failed to create collection')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteCollection = async (collectionId: string) => {
    if (!confirm('Are you sure you want to delete this collection?')) return

    try {
      await fetch(`/v1/collections/${collectionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      setMyCollections(myCollections.filter((c) => c.collection_id !== collectionId))
      setPublicCollections(publicCollections.filter((c) => c.collection_id !== collectionId))
    } catch (err) {
      console.error('Failed to delete collection:', err)
    }
    setMenuOpen(null)
  }

  const copyExportConfig = async (collectionId: string) => {
    try {
      const response = await api.exportCollection(collectionId, 'claude-desktop', token)
      await navigator.clipboard.writeText(JSON.stringify(response.config, null, 2))
      alert('Configuration copied to clipboard!')
    } catch (err) {
      console.error('Failed to export:', err)
    }
    setMenuOpen(null)
  }

  const collections = activeTab === 'public' ? publicCollections : myCollections

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Collections</h1>
            <p className="text-gray-600 mt-1">
              Browse curated skill collections or create your own
            </p>
          </div>
          {isAuthenticated && (
            <button
              onClick={() => setShowNewModal(true)}
              className="btn btn-primary flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Collection
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('public')}
            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'public'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Globe className="w-4 h-4 mr-2" />
            Public Collections
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'my'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="w-4 h-4 mr-2" />
            My Collections
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Collections list */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg mr-4" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : collections.length === 0 ? (
          <div className="card p-12 text-center">
            <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {activeTab === 'public' ? 'No public collections yet' : 'No collections yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {activeTab === 'public'
                ? 'Be the first to share a public collection!'
                : 'Create a collection to organize your MCP skills'}
            </p>
            {isAuthenticated && (
              <button
                onClick={() => setShowNewModal(true)}
                className="btn btn-primary"
              >
                Create a collection
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {collections.map((collection) => (
              <div
                key={collection.collection_id}
                className="card p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <Link
                    to={`/collections/${collection.collection_id}`}
                    className="flex-1"
                  >
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                        <FolderOpen className="w-6 h-6 text-primary-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-primary-600">
                            {collection.name}
                          </h3>
                          {collection.is_public ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                              <Globe className="w-3 h-3 mr-1" />
                              Public
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              <Lock className="w-3 h-3 mr-1" />
                              Private
                            </span>
                          )}
                        </div>
                        {collection.description && (
                          <p className="text-sm text-gray-500 mt-1">
                            {collection.description}
                          </p>
                        )}
                        {collection.owner_name && activeTab === 'public' && (
                          <p className="text-xs text-gray-400 mt-1 flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            by {collection.owner_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>

                  {activeTab === 'my' && (
                    <div className="relative">
                      <button
                        onClick={() =>
                          setMenuOpen(
                            menuOpen === collection.collection_id
                              ? null
                              : collection.collection_id
                          )
                        }
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      {menuOpen === collection.collection_id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button
                            onClick={() => copyExportConfig(collection.collection_id)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy config
                          </button>
                          <Link
                            to={`/collections/${collection.collection_id}`}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View details
                          </Link>
                          <button
                            onClick={() => handleDeleteCollection(collection.collection_id)}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Items preview */}
                <div className="mt-4">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <Package className="w-4 h-4 mr-1" />
                    {collection.items.length} skill{collection.items.length !== 1 ? 's' : ''}
                  </div>
                  {collection.items.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {collection.items.slice(0, 5).map((item) => (
                        <Link
                          key={item.package_id}
                          to={`/skills/${item.package_slug || item.package_id}`}
                          className="inline-flex items-center px-2.5 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-700 transition-colors"
                        >
                          {item.package_name || item.package_id}
                        </Link>
                      ))}
                      {collection.items.length > 5 && (
                        <span className="inline-flex items-center px-2.5 py-1 text-sm text-gray-500">
                          +{collection.items.length - 5} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New collection modal */}
        {showNewModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Create Collection
              </h2>
              <form onSubmit={handleCreateCollection}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="My Skill Collection"
                    className="input w-full"
                    autoFocus
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="A collection of skills for..."
                    className="input w-full h-24 resize-none"
                  />
                </div>
                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newIsPublic}
                      onChange={(e) => setNewIsPublic(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Make this collection public
                    </span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500 ml-6">
                    Public collections can be discovered and used by other users
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewModal(false)
                      setNewName('')
                      setNewDescription('')
                      setNewIsPublic(false)
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!newName.trim() || creating}
                    className="btn btn-primary"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
